import React, { useEffect, useState } from 'react';

import moment from 'moment';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';

import { ScheduleService } from '../../components/services';
import saveFile from '../../helpers/saveFile';

const exportDialogContentStyle: React.CSSProperties = {
  maxHeight: 'calc(100vh - 8rem)',
  overflow: 'auto'
};

export interface ExportCalendarAssignmentsDialogProps {
  exportDialogVisible: boolean;
  onHideExportDialog: () => void;
  viewMode: 'schedule' | 'timesheet';
  selectedDateFilter: Date | null;
  departmentId: number | null;
}

export function ExportCalendarAssignmentsDialog(
  props: ExportCalendarAssignmentsDialogProps
) {
  const {
    exportDialogVisible,
    onHideExportDialog,
    viewMode,
    selectedDateFilter,
    departmentId
  } = props;

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startDateError, setStartDateError] = useState<string>('');
  const [endDateError, setEndDateError] = useState<string>('');
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [includeHoursSum, setIncludeHoursSum] = useState<boolean>(true);
  const [includeAbsences, setIncludeAbsences] = useState<boolean>(true);
  const [includeTasks, setIncludeTasks] = useState<boolean>(false);

  const toast = React.useRef<Toast>(null);

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getLastDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  useEffect(() => {
    if (exportDialogVisible) {
      const baseDate = selectedDateFilter || new Date();
      const firstDay = getFirstDayOfMonth(baseDate);
      const lastDay = getLastDayOfMonth(baseDate);

      setStartDate(firstDay);
      setEndDate(lastDay);
      setStartDateError('');
      setEndDateError('');

      if (viewMode === 'timesheet') {
        setIncludeHoursSum(true);
        setIncludeAbsences(true);
        setIncludeTasks(false);
      } else {
        setIncludeAbsences(true);
        setIncludeTasks(false);
      }
    }
  }, [exportDialogVisible, selectedDateFilter, viewMode]);

  const validateDates = (start: Date | null, end: Date | null) => {
    if (!start)
      return { startErr: 'Дата начала обязательна для заполнения', endErr: '' };
    if (!end)
      return {
        startErr: '',
        endErr: 'Дата окончания обязательна для заполнения'
      };
    if (start > end)
      return {
        startErr: 'Дата начала не может быть позже даты окончания.',
        endErr: 'Дата окончания не может быть раньше даты начала.'
      };
    return { startErr: '', endErr: '' };
  };

  const normalizeDateValue = (value: any): Date | null =>
    Array.isArray(value) ? value[0] ?? null : value ?? null;

  const handleStartDateChange = (e: any) => {
    const value = normalizeDateValue(e.value);
    setStartDate(value);
    const { startErr, endErr } = validateDates(value, endDate);
    setStartDateError(startErr);
    setEndDateError(endErr);
  };

  const handleEndDateChange = (e: any) => {
    const value = normalizeDateValue(e.value);
    setEndDate(value);
    const { startErr, endErr } = validateDates(startDate, value);
    setStartDateError(startErr);
    setEndDateError(endErr);
  };

  const handleExport = async () => {
    if (!departmentId) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите участок для экспорта',
        life: 3000
      });
      return;
    }

    if (startDateError || endDateError) return;

    setIsExporting(true);

    try {
      const start = moment(startDate).format('YYYY-MM-DD');
      const end = moment(endDate).format('YYYY-MM-DD');
      const isTimesheet = viewMode === 'timesheet';

      const response = await ScheduleService.exportSchedule({
        start,
        end,
        departmentId,
        timesheet: isTimesheet,
        includeAbsences,
        includeTasks,
        includeTotalHours: isTimesheet ? includeHoursSum : undefined
      });

      const fileName = `${
        isTimesheet ? 'timesheet' : 'shifts'
      }_${moment().format('YYYYMMDD')}.xls`;
      await saveFile(response.data, fileName, '');

      toast.current?.show({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Файл успешно экспортирован',
        life: 3000
      });

      onHideExportDialog();
    } catch (error: any) {
      console.error('Export error:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail:
          error.response?.data?.message ||
          error.message ||
          'Не удалось экспортировать файл',
        life: 5000
      });
    } finally {
      setIsExporting(false);
    }
  };

  const minDate = selectedDateFilter
    ? new Date(
        selectedDateFilter.getFullYear(),
        selectedDateFilter.getMonth(),
        1
      )
    : undefined;

  const maxDate = selectedDateFilter
    ? new Date(
        selectedDateFilter.getFullYear(),
        selectedDateFilter.getMonth() + 1,
        0
      )
    : undefined;

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header={viewMode === 'schedule' ? 'Экспорт графика' : 'Экспорт табеля'}
        className="calendarAssignments-dialog calendarAssignments-dialog--export"
        style={{
          width: 'min(600px, calc(100vw - 1rem))',
          maxWidth: 'calc(100vw - 1rem)',
          maxHeight: 'calc(100vh - 1rem)',
          minHeight: viewMode === 'schedule' ? '442px' : '456px'
        }}
        contentStyle={exportDialogContentStyle}
        modal
        visible={exportDialogVisible}
        onHide={() => {
          if (!isExporting) {
            onHideExportDialog();
          }
        }}
        resizable={false}
      >
        <div className="dialog-tabless-card">
          <Divider />

          <div className="taskClosingCode-card__column">
            <div className="flex flex-column">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="startDate" className="col p-0">
                    Дата начала<span className="required">*</span>:
                  </label>
                  <div className="col-8 p-0">
                    <Calendar
                      id="startDate"
                      value={startDate}
                      onChange={handleStartDateChange}
                      dateFormat="dd.mm.yy"
                      showIcon
                      placeholder="Выберите дату"
                      style={{ width: '100%' }}
                      minDate={minDate}
                      maxDate={maxDate}
                      nextIcon="none"
                      prevIcon="none"
                    />
                  </div>
                </div>
                <p
                  className={`taskClosingCode-card-errors card-errors ${
                    startDateError ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {startDateError || ' '}
                </p>
              </div>

              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="endDate" className="col p-0">
                    Дата завершения<span className="required">*</span>:
                  </label>
                  <div className="col-8 p-0">
                    <Calendar
                      id="endDate"
                      value={endDate}
                      onChange={handleEndDateChange}
                      dateFormat="dd.mm.yy"
                      showIcon
                      placeholder="Выберите дату"
                      style={{ width: '100%' }}
                      minDate={minDate}
                      maxDate={maxDate}
                      nextIcon="none"
                      prevIcon="none"
                    />
                  </div>
                </div>
                <p
                  className={`taskClosingCode-card-errors card-errors ${
                    endDateError ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {endDateError || ' '}
                </p>
              </div>

              {viewMode === 'timesheet' && (
                <div className="flex flex-row align-items-center gap-3 w-full">
                  <InputSwitch
                    checked={includeHoursSum}
                    onChange={(e) => setIncludeHoursSum(e.value!)}
                  />
                  <label className="col-fixed p-3" htmlFor="includeHoursSum">
                    Выгружать сумму часов по сотрудникам
                  </label>
                </div>
              )}

              <div className="flex flex-row align-items-center gap-3 w-full">
                <InputSwitch
                  checked={includeAbsences}
                  onChange={(e) => setIncludeAbsences(e.value!)}
                />
                <label className="col-fixed p-3" htmlFor="includeAbsences">
                  Выгружать отсутствия сотрудников
                </label>
              </div>

              <div className="flex flex-row align-items-center gap-3 w-full">
                <InputSwitch
                  checked={includeTasks}
                  onChange={(e) => setIncludeTasks(e.value!)}
                />
                <label className="col-fixed p-3" htmlFor="includeTasks">
                  Выгружать наряды сотрудников
                </label>
              </div>
            </div>
          </div>

          <div className="default-card__buttons">
            <div className="flex gap-3">
              <Button
                type="submit"
                label="Экспорт"
                icon="pi pi-download"
                onClick={handleExport}
                disabled={isExporting}
                loading={isExporting}
              />
              <Button
                label="Отмена"
                icon="pi pi-times"
                className="p-button-secondary"
                onClick={onHideExportDialog}
              />
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
